import React, { useState, useMemo, useCallback, useEffect, useId, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import getFlatRows from '../helpers/get-flat-rows';
import getItemCheckState from '../helpers/get-item-check-state';
import filterTree from '../helpers/filter-tree';
import TreeSelectContext from '../context/tree-select-context';
import useTreeSelectData from '../hooks/use-tree-select-data';
import { SELECT_ALL_ID, flatRowTypes, itemCheckStates, sizes } from '../constants';

const TreeSelectProvider = ({
  id: inputId,
  items,
  value,
  searchPlaceholder,
  selectAllLabel,
  noResultsText,
  disabled = false,
  size = sizes.MEDIUM,
  invalid = false,
  clearable = true,
  withSelectAll = true,
  initialExpandedIds,
  children,
  placeholder,
  getSelectionLabel,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [manualExpandedIds, setExpandedIds] = useState(() => new Set(initialExpandedIds));
  const [manualCollapsedIds, setCollapsedIds] = useState(new Set());
  const [activeId, setActiveId] = useState(null);

  const isFiltering = useMemo(() => Boolean(inputValue.trim()), [inputValue]);
  const selectedIds = useMemo(() => new Set(value), [value]);

  const listboxId = useId();

  const inputRef = useRef(null);
  const listRef = useRef(null);

  const {
    idToItem,
    searchValues,
    itemAncestors,
    descendantItemIds,
    selectableDescendantItemIds,
    selectableIds,
  } = useTreeSelectData(items);

  const { visibleItems, autoExpandedIds } = useMemo(
    () =>
      filterTree({
        items,
        query: inputValue,
        searchValues,
        descendantItemIds,
        itemAncestors,
        idToItem,
      }),
    [items, inputValue, idToItem, searchValues, itemAncestors, descendantItemIds]
  );

  const checkStateMap = useMemo(
    () => getItemCheckState(idToItem, selectableDescendantItemIds, selectedIds),
    [idToItem, selectableDescendantItemIds, selectedIds]
  );

  const selectAllState = useMemo(() => {
    if ([...selectableIds].length === 0) return itemCheckStates.UNCHECKED;
    const someChecked = [...selectableIds].some((id) => selectedIds.has(id));
    if (!someChecked) return itemCheckStates.UNCHECKED;
    const allChecked = [...selectableIds].every((id) => selectedIds.has(id));
    return allChecked ? itemCheckStates.CHECKED : itemCheckStates.INDETERMINATE;
  }, [selectableIds, selectedIds]);

  const expandedIds = useMemo(() => {
    if (!isFiltering) return manualExpandedIds;
    const next = new Set(manualExpandedIds);
    autoExpandedIds.forEach((id) => {
      if (!manualCollapsedIds.has(id)) next.add(id);
    });
    return next;
  }, [autoExpandedIds, isFiltering, manualExpandedIds, manualCollapsedIds]);

  const flatRows = useMemo(
    () => getFlatRows(visibleItems, expandedIds),
    [visibleItems, expandedIds]
  );

  const flatRowsWithSelectAll = useMemo(() => {
    if (!withSelectAll || isFiltering) return flatRows;

    const selectAllRow = {
      type: flatRowTypes.ITEM,
      id: SELECT_ALL_ID,
      item: { id: SELECT_ALL_ID, value: SELECT_ALL_ID, label: selectAllLabel },
      depth: 0,
    };

    return [selectAllRow, { type: flatRowTypes.DIVIDER, id: uuid() }, ...flatRows];
  }, [flatRows, withSelectAll, isFiltering, selectAllLabel]);

  const navigableRows = useMemo(
    () =>
      flatRowsWithSelectAll.filter((row) => row.type === flatRowTypes.ITEM && !row.item.disabled),
    [flatRowsWithSelectAll]
  );

  const activeRowIdx = useMemo(
    () => navigableRows.findIndex((row) => row.id === activeId),
    [activeId, navigableRows]
  );

  const isActiveRowExist = activeRowIdx !== -1;
  const isFirstRow = activeRowIdx === 0;
  const isLastRow = activeRowIdx === navigableRows.length - 1;

  const navigateToNext = useCallback(() => {
    if (isActiveRowExist && !isLastRow) {
      setActiveId(navigableRows[activeRowIdx + 1].id);
    }
  }, [navigableRows, activeRowIdx, isActiveRowExist, isLastRow]);

  const navigateToPrev = useCallback(() => {
    if (isActiveRowExist && !isFirstRow) {
      setActiveId(navigableRows[activeRowIdx - 1].id);
    }
  }, [navigableRows, activeRowIdx, isActiveRowExist, isFirstRow]);

  const navigateToFirst = useCallback(() => {
    if (navigableRows.length > 0) {
      setActiveId(navigableRows[0].id);
    }
  }, [navigableRows]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setInputValue('');
    setActiveId(null);
    setExpandedIds(new Set(initialExpandedIds));
  }, [initialExpandedIds]);

  const handleItemToggle = useCallback(
    (item, checked) => {
      const next = new Set(selectedIds);
      const toggleItem = (id) => {
        if (checked) next.add(id);
        else next.delete(id);
      };
      if (item.items) {
        const descendantIds = selectableDescendantItemIds.get(item.id);
        descendantIds?.forEach((id) => {
          toggleItem(id);
        });
        onChange(Array.from(next));
        return;
      }
      toggleItem(item.id);
      onChange(Array.from(next));
    },
    [selectedIds, onChange, selectableDescendantItemIds]
  );

  const handleSelectAllToggle = useCallback(
    (checked) => {
      onChange(checked ? [...selectableIds] : []);
    },
    [selectableIds, onChange]
  );

  const handleExpandToggle = useCallback(
    (id) => {
      if (expandedIds.has(id)) {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        if (isFiltering) {
          setCollapsedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        }
      } else {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
        setCollapsedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [expandedIds, isFiltering]
  );

  useEffect(() => {
    if (!isFiltering) setCollapsedIds(new Set());
  }, [isFiltering]);

  return (
    <TreeSelectContext.Provider
      value={{
        activeId,
        checkStateMap,
        selectAllState,
        isOpen,
        inputRef,
        listRef,
        listboxId,
        inputValue,
        selectionCount: value.length,
        searchPlaceholder,
        disabled,
        clearable,
        size,
        invalid,
        flatRows: flatRowsWithSelectAll,
        noResultsText,
        isFirstRow,
        idToItem,
        itemAncestors,
        expandedIds,
        inputId,
        placeholder,
        onBlur,
        onFocus,
        onKeyDown,
        setActiveId,
        getSelectionLabel,
        closeDropdown,
        setIsOpen,
        setInputValue,
        onItemToggle: handleItemToggle,
        onSelectAllToggle: handleSelectAllToggle,
        onExpandToggle: handleExpandToggle,
        onChange,
        navigateToNext,
        navigateToPrev,
        navigateToFirst,
      }}
    >
      {children}
    </TreeSelectContext.Provider>
  );
};

export default TreeSelectProvider;
